import type { ReactNode } from 'react';
import * as React from 'react';
import { debounce, isEmpty } from 'lodash';
import MaskInput from 'react-maskinput';

import {
  IControlError,
  IControlSchema,
  IControlSchemaBase,
} from '../../core/Controls';
import {
  dateFormat,
  phoneFix,
  TextBoxValidate,
} from '../../core/validation/validate';
import {
  buildUrl,
  clearNonNumberCharacters,
  Component,
  createCounterMessage,
  getDigits,
  I18NString,
  trimString,
} from '../helpers/Utils';
import { IDispatcher } from '../helpers/Dispatcher';

import Note from './components/Note';
import Field from './components/Field';
import ErrorAlert from './components/ErrorAlert';
import { createI18n } from './components/I18n';
import { Dictionary } from '../../types/Dictionary';

export interface ITextBoxSchema
  extends Omit<IControlSchemaBase, 'data' | 'defaultValue'> {
  description?: I18NString;
  notification?: I18NString;
  maxLength?: number;
  minLength?: number;
  disabled?: boolean;
  placeholder?: string;
  defaultValue?: {
    ru: string;
    en: string;
  };
  data?: {
    url: string;
  };
  isInteger?: boolean;
  lines?: number;
  value?: string;
  min?: number;
  max?: number;
  label?: string;
  extra?: any;
  mask?: string;
  maskChar?: string;
  size?: number;
}

export interface ITextBoxProps {
  schema: ITextBoxSchema;
  api: any;
  dispatcher?: IDispatcher;
  errors: Dictionary<IControlError | undefined>;
  visited: Dictionary<boolean>;
  controlValue?: any;
  values?: any;
}

interface OkpdTnvedState {
  id: string;
  name: string;
  code: string;
}

interface IState {
  value: string | OkpdTnvedState;
  openInfo: boolean;
  openNotification: boolean;
  okpdOrTnvedError: boolean;
  okvedMessage: string | null;
}

const Help = ({ onClick }) => (
  <span onClick={onClick} className="input-group-addon btn btn-default">
    <span className="glyphicon glyphicon-question-sign" />
  </span>
);

const Notification = ({ onClick }) => (
  <span onClick={onClick} className="input-group-addon btn btn-default">
    <span className="glyphicon glyphicon-info-sign" />
  </span>
);

const InputWrapper = ({
  inputField,
  maxLength,
  minLength,
  messageValue,
  i18nControls,
  api,
  dispatcher,
  errors,
  visited,
  type,
  onClick,
  openNotification,
  label,
  classNames,
}): JSX.Element => (
  <div>
    <div className="input-group" style={{ width: '100%' }}>
      <div className={classNames} style={{ width: '100%' }}>
        {inputField}
        {maxLength && (maxLength > 0 || minLength > 0) && i18nControls && (
          <div className="input-counter">
            {createCounterMessage(messageValue, maxLength, minLength)}
          </div>
        )}
      </div>
      {i18nControls
        ? createI18n(
            i18nControls,
            api,
            dispatcher,
            errors,
            visited,
            type,
            onClick,
            openNotification,
          )
        : null}
      {label && <span className="input-group-addon">{label}</span>}
      {!i18nControls && openNotification && (
        <Notification onClick={openNotification} />
      )}
      {!i18nControls && onClick && <Help onClick={onClick} />}
    </div>
    {maxLength && (maxLength > 0 || minLength > 0) && !i18nControls && (
      <div className="input-counter">
        {createCounterMessage(messageValue, maxLength, minLength)}
      </div>
    )}
  </div>
);

export class TextBox extends Component<ITextBoxProps, IState> {
  private input: HTMLInputElement | HTMLTextAreaElement;

  state = {
    value: null,
    openInfo: false,
    openNotification: true,
    okpdOrTnvedError: false,
    okvedMessage: null,
  };

  constructor(props) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onClick = this.onClick.bind(this);
    this.openNotification = this.openNotification.bind(this);
    this.componentDidMount = this.componentDidMount.bind(this);
    this.stopDefaultEvents = this.stopDefaultEvents.bind(this);
  }

  public componentDidMount() {
    const {
      schema: { value },
      errors: existingError,
      values,
      // controlValue
    } = this.props;

    if (values) {
      this.setState({ value: values });
      return;
    }
    // if (controlValue) {
    // 	this.setState({ value: controlValue });
    // 	return;
    // }

    if (!isEmpty(value)) {
      this.props.dispatcher.onVisit(this.props.schema.id);
    }
    this.setState({ value });

    if (!isEmpty(existingError)) {
      return;
    }

    const error = TextBoxValidate(value, this.props.schema);
    this.props.dispatcher.setError(
      this.props.schema.id,
      this.input?.value,
      error?.isError ? { message: error.message } : undefined,
      false,
    );
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillUpdate?(
    nextProps: Readonly<ITextBoxProps>,
    nextState: Readonly<IState>,
  ): void {
    if (
      this.props?.controlValue !== nextProps?.controlValue &&
      nextState.value !== nextProps?.controlValue
    ) {
      // TODO: !!!
      // eslint-disable-next-line react/no-will-update-set-state
      this.setState({ value: nextProps.controlValue });
    }
    if (
      this.props.schema.value !== nextProps.schema.value &&
      nextState.value !== nextProps.schema.value
    ) {
      // TODO: !!!
      // eslint-disable-next-line react/no-will-update-set-state
      this.setState({ value: nextProps.schema.value });
    }
  }

  protected onChange(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    let { value } = event.target;
    if (this.props.schema?.extra?.valueType?.toUpperCase() === 'PHONE') {
      value = phoneFix(value);
    }
    if (
      this.props.schema?.extra?.valueType?.toUpperCase() === 'DATEMONTHANDYEAR'
    ) {
      value = dateFormat(value);
    }
    event.target.value = value;

    const trimmedValue = trimString(this.props.schema.maxLength, value);

    this.setState({
      value: trimmedValue,
    });

    this.debouncedOnChange(trimmedValue);
  }

  protected onBlur() {
    if (!this.input) {
      return;
    }
    const value =
      typeof this.input.value === 'string' && this.input.value.trim();

    const error = TextBoxValidate(value, this.props.schema);
    this.props.dispatcher.onBlur(
      this.props.schema.id,
      value,
      !error,
      error && error.isError && { message: error.message },
    );
    this.props.dispatcher.onVisit(this.props.schema.id);
  }

  onClick() {
    this.setState(({ openInfo }) => ({
      openInfo: !openInfo,
    }));
  }

  private onNumberInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = clearNonNumberCharacters(event.target.value);

    // TODO: Need TEST!!!
    // this.setState({ ...this.state, value });
    this.setState({ value });
  };

  private onNumberChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let value = trimString(
      this.props.schema.maxLength,
      clearNonNumberCharacters(event.target.value),
    );

    if (this.props.schema?.extra?.valueType === 'snils_individual') {
      value = value.replace(/[^0-9]/g, '');
    }

    // TODO: Need TEST!!!
    // this.setState({ ...this.state, value });
    this.setState({ value });
    this.debouncedOnChange(value);
  };

  openNotification() {
    this.setState(({ openNotification }) => ({
      openNotification: !openNotification,
    }));
  }

  // Просилось в грантах запретить вставку у числовых значений
  // eslint-disable-next-line class-methods-use-this
  stopDefaultEvents(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  private debouncedOnChange = debounce(this.checkOnBlur, 1000, {
    leading: false,
  });

  private checkOnBlur(value: string) {
    if (value !== this.state.value) {
      return;
    }
    const error = TextBoxValidate(value, this.props.schema);
    this.props.dispatcher.onBlur(
      this.props.schema.id,
      this.state.value,
      error && error.isError,
      error && error.isError && { message: error.message },
    );
  }

  handleValueForOkpdTnved(data, codeValue) {
    if (data !== null) {
      if (typeof data?.correct !== 'undefined') {
        //Старый способ АПИ
        if (data?.correct === false) {
          this.setState(({ value, okpdOrTnvedError }) => ({
            value: codeValue.replace(/(\d*\.?\d*)[^0-9]*$/, '$1'),
            okvedMessage: 'Значение некорретно',
            okpdOrTnvedError: true,
          }));
        } else {
          this.setState(({ value, okpdOrTnvedError }) => ({
            value: codeValue.replace(/(\d*\.?\d*)[^0-9]*$/, '$1'),
            okpdOrTnvedError:
              typeof data?.correct !== 'undefined'
                ? data?.correct === false
                : false,
            okvedMessage: data.message,
          }));
          this.debouncedOnChange(codeValue);
        }
      } else {
        this.setState(({ value, okpdOrTnvedError }) => ({
          value: {
            id: data.id ?? codeValue,
            name: data.name ?? data.message,
            code: codeValue,
          },
          okpdOrTnvedError:
            typeof data?.correct !== 'undefined'
              ? data?.correct === false
              : false,
        }));
        this.debouncedOnChange(this.state.value);
      }
    } else {
      this.setState(({ value, okpdOrTnvedError }) => ({
        value: {
          id: null,
          code: null,
          name: 'Значение некорретно',
        },
        okpdOrTnvedError: true,
      }));
    }
  }

  handleSetErrorForOkpdTnved() {
    this.setState(({ value, okpdOrTnvedError }) => ({
      value: {
        id: null,
        code: null,
        name: 'Ошибка при загрузке',
      },
      okpdOrTnvedError: true,
    }));
  }

  setOKPDorTNVEDState = (event, columnField: string) => {
    const { value } = event.target;

    const {
      api,
      schema: { data },
    } = this.props;

    const dataUrl = `${data.url}${value.replace(/(\d*\.?\d*)[^0-9]*$/, '$1')}`;

    if (getDigits(value).length > 0) {
      api.moduleApi.get(`${api.options.baseUrl}${dataUrl}`, null, {
        success: (response) =>
          this.handleValueForOkpdTnved(response.data, value),
        error: () => this.handleSetErrorForOkpdTnved(),
      });
    } else {
      this.setState(({ value }) => ({
        value: '',
      }));
      this.debouncedOnChange(getDigits(value));
    }
  };

  public render() {
    const { schema, errors, visited, api, dispatcher } = this.props;

    const {
      extra,
      maxLength,
      minLength = 0,
      disabled,
      mask,
      maskChar,
      size,
      type,
      label,
      notification,
    } = schema;

    const { value } = this.state;

    const isSpecialField =
      extra?.valueType === 'inn_individual' ||
      extra?.valueType === 'snils_individual';

    const errorText = isSpecialField
      ? errors?.[schema?.id]?.message
      : visited?.[schema?.id] && errors?.[schema?.id]?.message;
    let input: null | ReactNode;

    let className = 'default';
    if (value && !errorText) {
      className = 'has-success';
    } else if (errorText) {
      className = 'has-error';
    }

    const InputTag = mask ? MaskInput : 'input';

    const maskProps = {
      mask,
      maskChar,
      size,
      alwaysShowMask: true,
    };

    const inputWrapperProps = {
      i18nControls: extra?.i18nControls,
      maxLength,
      minLength,
      errors,
      visited,
      type,
      api,
      dispatcher,
      messageValue: value?.code || value,
      openNotification: notification ? this.openNotification : null,
      label,
      classNames: className,
    };

    if (type.toUpperCase() === 'TEXTAREA') {
      const textarea = (
        <textarea
          // @ts-ignore
          name={schema?.field}
          style={{ resize: 'vertical' }}
          className="form-control"
          placeholder={
            schema?.extra?.placeholder?.ru ?? schema?.placeholder ?? ''
          }
          onChange={this.onChange}
          // onBlur={this.onBlur}
          value={value || (schema?.defaultValue?.ru ?? '')}
          ref={(node) => {
            this.input = node;
          }}
          rows={schema.lines}
          disabled={schema.disabled}
        />
      );

      input = (
        <InputWrapper
          {...inputWrapperProps}
          inputField={textarea}
          messageValue={value || (schema?.defaultValue?.ru ?? '')}
          onClick={schema?.description ? this.onClick : null}
        />
      );
    } else if (type.toUpperCase() === 'NUMBER') {
      const number = (
        <InputTag
          // @ts-ignore
          name={schema?.field}
          type="text"
          className="form-control"
          inputMode="decimal"
          placeholder={
            schema?.extra?.placeholder?.ru ?? schema?.placeholder ?? ''
          }
          onChange={this.onNumberChange}
          onInput={this.onNumberInput}
          // onBlur={this.onBlur}
          onPaste={this.stopDefaultEvents}
          value={value || ''}
          ref={(node) => {
            this.input = node;
          }}
          disabled={schema.disabled}
          {...maskProps}
        />
      );

      input = (
        <InputWrapper
          {...inputWrapperProps}
          inputField={number}
          onClick={schema?.description ? this.onClick : null}
        />
      );
    } else if (type.toUpperCase() === 'FLOAT') {
      const float = (
        <InputTag
          // @ts-ignore
          name={schema?.field}
          type="text"
          className="form-control"
          inputMode="decimal"
          placeholder={
            schema?.extra?.placeholder?.ru ?? schema?.placeholder ?? ''
          }
          onChange={this.onNumberChange}
          onInput={this.onNumberInput}
          // onBlur={this.onBlur}
          onPaste={this.stopDefaultEvents}
          value={value || ''}
          ref={(node) => {
            this.input = node;
          }}
          disabled={schema.disabled}
          {...maskProps}
        />
      );

      input = (
        <InputWrapper
          {...inputWrapperProps}
          inputField={float}
          onClick={schema?.description ? this.onClick : null}
        />
      );
    } else if (extra && extra.valueType === 'okpd') {
      const okpd = (
        <InputTag
          // @ts-ignore
          name={schema?.field}
          type="text"
          className="form-control editor edit-text"
          alwaysShowMask
          onChange={(event) => this.setOKPDorTNVEDState(event, schema.id)}
          value={value?.code || ''}
          ref={(node) => {
            this.input = node;
          }}
          disabled={schema.disabled}
          {...maskProps}
        />
      );

      input = (
        <InputWrapper
          {...inputWrapperProps}
          inputField={okpd}
          onClick={schema?.description ? this.onClick : null}
        />
      );
    } else if (extra && extra.valueType === 'tnved') {
      const tnved = (
        <InputTag
          // @ts-ignore
          name={schema?.field}
          type="text"
          className="form-control editor edit-text"
          alwaysShowMask
          onChange={(event) => this.setOKPDorTNVEDState(event, schema.id)}
          value={value?.code || ''}
          ref={(node) => {
            this.input = node;
          }}
          disabled={schema.disabled}
          {...maskProps}
        />
      );

      input = (
        <InputWrapper
          {...inputWrapperProps}
          inputField={tnved}
          onClick={schema?.description ? this.onClick : null}
        />
      );
    } else if (extra && extra.valueType === 'okved') {
      //В значении лежит чисто сам код
      const okved = (
        <InputTag
          // @ts-ignore
          name={schema?.field}
          type="text"
          className="form-control editor edit-text"
          showStartChars={false}
          onChange={(event) => this.setOKPDorTNVEDState(event, schema.id)}
          value={value || ''}
          ref={(node) => {
            this.input = node;
          }}
          disabled={schema.disabled}
          {...maskProps}
        />
      );

      input = (
        <InputWrapper
          {...inputWrapperProps}
          inputField={okved}
          onClick={schema?.description ? this.onClick : null}
        />
      );
    } else {
      const textbox = (
        <InputTag
          // @ts-ignore
          name={schema?.field}
          type="text"
          className="form-control"
          placeholder={
            schema?.extra?.placeholder?.ru ?? schema?.placeholder ?? ''
          }
          onChange={this.onChange}
          // onBlur={this.onBlur}
          value={value || ''}
          title={value}
          disabled={schema.disabled}
          ref={(node) => {
            this.input = node;
          }}
          {...maskProps}
        />
      );

      input = (
        <InputWrapper
          {...inputWrapperProps}
          inputField={textbox}
          onClick={schema?.description && !disabled ? this.onClick : null}
        />
      );
    }

    return (
      // <Field {...this.props.schema} status={className}>
      <Field {...this.props.schema}>
        {extra && extra.hint && (
          <div
            className="alert alert-info hint"
            dangerouslySetInnerHTML={{ __html: extra.hint }}
          />
        )}
        {input}
        {(this.state.value?.name ?? this.state.okvedMessage) && (
          <span
            className={`label tnved-okpd-label label-${
              this.state.okpdOrTnvedError ? 'danger' : 'success'
            }`}
          >
            {this.state.value?.name ?? this.state.okvedMessage}
          </span>
        )}
        {this.props.schema?.notification && this.state.openNotification && (
          <div
            className="alert alert-warning"
            role="alert"
            dangerouslySetInnerHTML={{
              __html: this.tr(this.props.schema.notification),
            }}
          />
        )}
        {this.props.schema?.description && this.state.openInfo && (
          <div
            className="alert alert-warning"
            role="alert"
            dangerouslySetInnerHTML={{
              __html: this.tr(this.props.schema.description),
            }}
          />
        )}
        <ErrorAlert errorMessage={errorText} />
        {extra && extra.note && (
          <Note content={extra.note} dispatcher={this.props.dispatcher} />
        )}
      </Field>
    );
  }
}

export function createTextBox(
  schema: IControlSchema,
  api: any,
  dispatcher: IDispatcher = null,
  error: Dictionary<IControlError | undefined>,
  visited: Dictionary<boolean>,
): React.ReactElement<any> {
  return (
    <TextBox
      schema={schema as ITextBoxSchema}
      api={api}
      dispatcher={dispatcher}
      errors={error}
      visited={visited}
    />
  );
}
